import { useEffect, useMemo, useState } from 'react'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { Profile } from 'types'

export const _userProfilesMap = new Map<number, Profile | null>()

/**
 * Holds a reference to the components that are to be notified when we have the users that they are interested in.
 */
let listeners = new Map<number, Array<(newUserMap: Map<number, Profile>) => void>>()

/**
 * Given a userId, call the backend to get the user's `Profile` and cache it in a `Map` until the page is reloaded.
 *
 * Note: The `Map` is stored in a module-level variable, so it's shared across all instances of this hook
 *
 * Note: Will not call the backend again if a request has already been made for the same userId, even if the request has not
 * resolved yet
 * @param id a user ID
 * @param onUpdate callback that will be invoked when the `Profile` for that userId is returned
 */
const fetchUserProfile = (id: number, onUpdate: (newUserMap: Map<number, Profile>) => void) => {
  // The user exists in the global Map (not undefined or null)
  if (_userProfilesMap.get(id)) {
    onUpdate(mapWithoutNulls(_userProfilesMap))
    return
  }

  // We don't have the user or we are fetching the user, so we register the callback to be called when we have the user
  const currentListeners = listeners.get(id) ?? []
  listeners.set(id, [...currentListeners, onUpdate])

  // We don't have the user and we haven't started fetching it yet
  if (!_userProfilesMap.has(id)) {
    // We prevent multiple fetches by setting the user id in the global Map to null
    _userProfilesMap.set(id, null)
    client.getUserCard(id).then((response) => {
      if (response.type === SUCCESS_RESPONSE_TYPE) {
        _userProfilesMap.set(id, response.data.user)
        // Notify all listeners that we have the data they wanted
        listeners.get(id)?.forEach((listener) => {
          listener(mapWithoutNulls(_userProfilesMap))
        })
        // Clear listeners to avoid notifying them more than once
        listeners.set(id, [])
      } else if (_userProfilesMap.get(id) === null) {
        // We received an error, so we should allow new requests to proceed. Maybe they'll succeed next time
        _userProfilesMap.delete(id)
      }
    })
  }
}
/**
 * Given a `Map`, return a copy stripped of all keys that have `null` as values
 */
function mapWithoutNulls<K, T>(map: Map<K, T | null>): Map<K, T> {
  return new Map(Array.from(map.entries()).filter(([, value]) => value !== null) as Array<[K, T]>)
}

/**
 * A hook that returns a `Map` of user IDs to user `Profile`s
 * @param userIDs An array or a set (arrays will be coerced into sets anyways) of user IDs you are interested in
 * @returns a `Map` of user IDs to user `Profile`s. The `Map` will be updated as the user `Profile`s are fetched, but
 * changes to the `Profile`s in the backend will not be reflected in the `Map` returned by this hook until the page is
 * refreshed
 */
export const useUserProfiles = (userIDs: number[] | Set<number>): Map<number, Profile> => {
  const userIdsSet = useMemo(() => (userIDs instanceof Set ? userIDs : new Set(userIDs)), [userIDs])

  const [userProfilesMap, setUserProfilesMap] = useState(new Map<number, Profile>())

  useEffect(() => {
    userIdsSet.forEach((id) => {
      if (!userProfilesMap.has(id)) {
        fetchUserProfile(id, setUserProfilesMap)
      }
    })
  }, [userIdsSet, userProfilesMap])

  return userProfilesMap
}
